/**
 * La scheda di un professionista sul telefono (7/10/2026): risponde a "chi è,
 * come lo raggiungo, e cosa gli dobbiamo?", come la scheda di un contatto
 * dell'iPhone.
 *
 * Prima, a 390px: l'avatar e il nome in una riga lunga, quattro numeri in
 * griglia (la tariffa "Riservato", "0 progetti attivi"), quattro linguette in
 * inglese ("Overview") e i dati in blocchi EMAIL / TELEFONO / PEC con il
 * trattino quando mancano.
 * Qui:
 *   1. iniziali, nome, dove sta;
 *   2. i gesti: chiama, scrivi, WhatsApp, mappa (spenti se il dato manca);
 *   3. i soldi: quanto gli abbiamo pagato e quanto gli dobbiamo ancora;
 *   4. le ultime fatture ricevute, i progetti, i recapiti, le competenze, le
 *      note e i documenti, ognuno un gruppo; i vuoti non si mostrano.
 * "Carica un documento" in fondo, come prima. Modificare, disattivare ed
 * eliminare restano al computer. Stessa chiamata e stessa cache del computer.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { fmtEur } from '../../lib/utils';
import { Icon } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MActions, MEmpty, MGroup, MIdentity, MMore, MPage, MRow, iniziali, leggibile } from '../../components/mobile/MobileKit';
import './suppliers-mobile.css';

interface SupplierDetail {
  id: string; fullName: string; vat: string | null; taxCode: string | null; email: string | null; phone: string | null;
  city: string | null; hourlyRate: string | null; dailyRate: string | null; active: boolean; notes: string | null;
  pec: string | null; addressLine: string | null; isPerson: boolean; ficSupplierId: number | null;
  skills: Array<{ skillId: string; level: number; skill: { name: string } }>;
  documents: Array<{ id: string; name: string; storageKey: string; uploadedAt: string }>;
  projectMembers: Array<{ id: string; role: string; hoursPlanned: number; project: { id: string; code: string; name: string; status: string; division: string } }>;
  alsoClient: { id: string; shortName: string; legalName: string } | null;
  payables: Array<{ id: string; number: string | null; issueDate: string; dueDate: string | null; total: string; status: string; description: string | null; category: string | null }>;
  payablesStats: { count: number; total: number; open: number };
}

/** "333 123 4567" → "393331234567" per WhatsApp; i fissi (con lo 0) no. */
function numeroWhatsApp(tel: string | null | undefined): string | null {
  if (!tel) return null;
  let n = tel.replace(/[^\d+]/g, '');
  if (n.startsWith('+')) n = n.slice(1);
  else if (n.startsWith('00')) n = n.slice(2);
  else if (n.startsWith('3') && n.length === 10) n = `39${n}`;
  if (n.startsWith('390')) return null;
  return n.length >= 10 ? n : null;
}

const giorno = (iso: string) => {
  const d = new Date(iso);
  const stessoAnno = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', ...(stessoAnno ? {} : { year: 'numeric' }) }).replace('.', '');
};
const STATO_FATTURA: Record<string, string> = { PAGATA: 'pagata', DA_PAGARE: 'da pagare', SCADUTA: 'scaduta', ANNULLATA: 'annullata', PARZIALE: 'pagata in parte' };
const statoFattura = (s: string) => STATO_FATTURA[s] ?? s.replace(/_/g, ' ').toLowerCase();
const aperta = (s: string) => s !== 'PAGATA' && s !== 'ANNULLATA';

export function FornitoreMobile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [tutte, setTutte] = useState(false);
  const [nota, setNota] = useState(false);

  const { data: s, isLoading, isError } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => api.get<SupplierDetail>(`/suppliers/${id}`),
    enabled: !!id,
    retry: false,
  });

  if (isError) return <MPage><MEmpty>Professionista non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !s) return <MPage><MEmpty>Carico il professionista…</MEmpty></MPage>;

  const nome = leggibile(s.fullName);
  const indirizzo = [s.addressLine, s.city].filter(Boolean).join(', ');
  const wa = numeroWhatsApp(s.phone);
  const fatture = tutte ? s.payables : s.payables.slice(0, 5);
  const tariffa = Number(s.hourlyRate ?? 0);
  const giornaliera = Number(s.dailyRate ?? 0);

  return (
    <div className="spm">
      <MPage>
        <MIdentity
          text={iniziali(nome)}
          title={nome}
          sub={
            <>
              {[s.isPerson ? 'Professionista' : 'Fornitore', s.city ? leggibile(s.city) : null].filter(Boolean).join(' · ')}
              {!s.active && <> · <span className="is-alert">non più attivo</span></>}
            </>
          }
        />

        <MActions
          azioni={[
            { label: 'Chiama', icon: 'phone', href: s.phone ? `tel:${s.phone.replace(/\s+/g, '')}` : null },
            { label: 'Scrivi', icon: 'mail', href: s.email ? `mailto:${s.email}` : null },
            { label: 'WhatsApp', icon: 'message', href: wa ? `https://wa.me/${wa}` : null },
            { label: 'Mappa', icon: 'pin', href: indirizzo ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(indirizzo)}` : null },
          ]}
        />

        <MGroup title="Soldi">
          <MRow icon="receipt" title="Pagato in tutto" sub={s.payablesStats.count ? `${s.payablesStats.count} ${s.payablesStats.count === 1 ? 'fattura ricevuta' : 'fatture ricevute'}` : 'nessuna fattura ricevuta'} trail={`€ ${fmtEur(s.payablesStats.total)}`} />
          <MRow
            icon="calendarClock"
            tone={s.payablesStats.open > 0 ? 'alert' : undefined}
            title="Da pagare"
            sub={s.payablesStats.open > 0 ? undefined : 'niente in sospeso'}
            trail={s.payablesStats.open > 0 ? `€ ${fmtEur(s.payablesStats.open)}` : '—'}
            trailTone={s.payablesStats.open > 0 ? 'alert' : 'mute'}
          />
          {showMargin && (tariffa > 0 || giornaliera > 0) && (
            <MRow icon="clock" title="Tariffa" sub={giornaliera > 0 ? `€ ${fmtEur(giornaliera)} al giorno` : undefined} trail={tariffa > 0 ? `€ ${fmtEur(tariffa)}/h` : '—'} />
          )}
        </MGroup>

        {s.payables.length > 0 && (
          <MGroup
            title="Fatture ricevute"
            count={s.payablesStats.count}
            note={s.payablesStats.count > s.payables.length ? `Qui le ultime ${s.payables.length}; i totali sopra sono su tutte.` : undefined}
          >
            {fatture.map((p) => (
              <MRow
                key={p.id}
                icon="invoice"
                tone={aperta(p.status) && p.dueDate && Date.parse(p.dueDate) < Date.now() ? 'alert' : undefined}
                title={p.description || p.category || (p.number ? `Fattura ${p.number}` : 'Fattura senza numero')}
                sub={[p.number ? `n. ${p.number}` : null, `del ${giorno(p.issueDate)}`, aperta(p.status) && p.dueDate ? `scade il ${giorno(p.dueDate)}` : null].filter(Boolean).join(' · ')}
                trail={`€ ${fmtEur(p.total)}`}
                trailSub={statoFattura(p.status)}
              />
            ))}
            {s.payables.length > 5 && <MMore label={tutte ? 'Mostra meno' : `Vedi tutte le ${s.payables.length}`} onClick={() => setTutte((v) => !v)} />}
          </MGroup>
        )}

        {s.projectMembers.length > 0 && (
          <MGroup title="Progetti" count={s.projectMembers.length}>
            {s.projectMembers.map((m) => (
              <MRow
                key={m.id}
                icon="cube"
                title={m.project.name.split(/\s+[—–-]\s+/).slice(1).join(' ') || m.project.name}
                sub={[m.role, m.hoursPlanned ? `${m.hoursPlanned} h pianificate` : null, m.project.status === 'CHIUSO' ? 'chiuso' : null].filter(Boolean).join(' · ')}
                onClick={() => navigate(`/projects/${m.project.id}`)}
              />
            ))}
          </MGroup>
        )}

        <MGroup title="Recapiti e dati fiscali">
          {s.phone && <MRow icon="phone" title={s.phone} sub="telefono" />}
          {s.email && <MRow icon="mail" title={s.email} sub="email" />}
          {s.pec && <MRow icon="mail" title={s.pec} sub="PEC" />}
          {indirizzo && <MRow icon="pin" title={leggibile(indirizzo)} sub="sede" />}
          {s.vat && <MRow icon="file" title={s.vat} sub="partita IVA" />}
          {s.taxCode && <MRow icon="file" title={s.taxCode} sub="codice fiscale" />}
          {!s.phone && !s.email && !s.pec && !indirizzo && !s.vat && !s.taxCode && <MEmpty>Nessun recapito in anagrafica.</MEmpty>}
        </MGroup>

        {s.skills.length > 0 && (
          <MGroup title="Competenze" count={s.skills.length}>
            {s.skills.map((sk) => <MRow key={sk.skillId} title={sk.skill.name} trail={`${sk.level} su 5`} trailTone="mute" />)}
          </MGroup>
        )}

        {(s.notes || s.alsoClient || s.ficSupplierId != null) && (
          <MGroup title="Altro">
            {s.notes && <MRow icon="pencil" title="Note interne" sub={s.notes.split('\n')[0]} onClick={() => setNota(true)} />}
            {s.alsoClient && <MRow icon="building" title="È anche un cliente" sub={leggibile(s.alsoClient.shortName || s.alsoClient.legalName)} onClick={() => navigate(`/clients/${s.alsoClient!.id}`)} />}
            {s.ficSupplierId != null && <MRow icon="refresh" title="Anagrafica da FattureInCloud" sub="i dati arrivano da lì: quelli che mancano mancano anche là" />}
          </MGroup>
        )}

        <MGroup title="Documenti" count={s.documents.length || undefined}>
          {s.documents.length
            ? s.documents.map((d) => <MRow key={d.id} icon="file" title={d.name} sub={`caricato il ${giorno(d.uploadedAt)}`} />)
            : <MEmpty>Nessun documento: contratti e accordi si caricano con il tasto qui sotto.</MEmpty>}
        </MGroup>

        <DesktopOnly
          cosa="Modificare, disattivare o eliminare il professionista"
          perche="sono operazioni di anagrafica da fare con calma. Dal telefono si chiama, si scrive e si carica un documento."
        ><></></DesktopOnly>
      </MPage>

      <div className="m-actionbar m-only">
        <button className="btn btn-primary" onClick={() => useModalStore.getState().open('uploadDoc', { scope: 'supplier', scopeId: s.id })}>
          <Icon name="upload" size={12} /> Carica un documento
        </button>
      </div>

      <MobileSheet open={nota} onClose={() => setNota(false)} title="Note interne">
        <div className="spm-testo">{s.notes}</div>
      </MobileSheet>
    </div>
  );
}
